/**
 * Compass sidebar. Collapsible (w-16 ↔ w-56). Logo + account summary +
 * navigation + version footer. Cmd/Ctrl-click a nav item to open in a
 * new workspace split.
 */
import { useNavigate } from 'react-router-dom'
import { CompassLogo } from '@/components/CompassLogo'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSettingsStore } from '@/stores/settingsStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { NAV_ITEMS, getViewPath } from '@/lib/workspace/registry'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { SidebarAccountSummary } from './SidebarAccountSummary'

interface SidebarProps {
  /** When true, render inside the mobile Sheet instead of as a fixed desktop rail. */
  embedded?: boolean
  /** Called after a nav item is chosen — used to dismiss the mobile drawer. */
  onNavigate?: () => void
}

export function Sidebar({ embedded = false, onNavigate }: SidebarProps) {
  const storedCollapsed = useSettingsStore((s) => s.sidebarCollapsed)
  const toggle = useSettingsStore((s) => s.toggleSidebar)
  const collapsed = embedded ? false : storedCollapsed

  const navigateToView = useWorkspaceStore((s) => s.navigateToView)
  const openInNewSplit = useWorkspaceStore((s) => s.openInNewSplit)
  const focusedActiveViewId = useWorkspaceStore((s) => {
    if (!s.focusedPanelId) return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ad-hoc tree walk over the workspace node union; narrowing here adds no safety
    const find = (n: any): any => {
      if (n.type === 'panel') return n.id === s.focusedPanelId ? n : null
      return find(n.children[0]) ?? find(n.children[1])
    }
    const panel = find(s.rootNode)
    if (!panel) return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- panel.views element shape comes from the untyped walk above
    const active = panel.views.find((v: any) => v.instanceId === panel.activeViewInstanceId)
    return active?.viewId ?? null
  })

  const navigate = useNavigate()

  const onNavClick = (e: React.MouseEvent, viewId: string) => {
    e.preventDefault()
    if (e.ctrlKey || e.metaKey) {
      const path = openInNewSplit(viewId, 'horizontal')
      navigate(path, { replace: true })
    } else {
      const path = navigateToView(viewId)
      navigate(path, { replace: true })
    }
    onNavigate?.()
  }

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        className={cn(
          'flex flex-col border-r border-border bg-card transition-all duration-200',
          embedded
            ? 'relative h-full w-full'
            : cn(
                'fixed inset-y-0 left-0 z-30',
                collapsed ? 'w-16' : 'w-56',
              ),
        )}
      >
        {/* Logo + collapse toggle */}
        <div className="flex h-14 items-center justify-between border-b border-border px-3">
          <div className={cn('flex items-center gap-2 overflow-hidden', collapsed && 'justify-center w-full')}>
            <CompassLogo className="h-7 w-7 shrink-0" />
            {!collapsed && (
              <span className="font-mono text-base tracking-tight">Compass</span>
            )}
          </div>
          {!collapsed && !embedded && (
            <button
              onClick={toggle}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Re-expand button when collapsed */}
        {collapsed && !embedded && (
          <button
            onClick={toggle}
            className="border-b border-border py-1 text-muted-foreground hover:text-foreground"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4 mx-auto" />
          </button>
        )}

        {/* Account summary */}
        <SidebarAccountSummary collapsed={collapsed} />

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin space-y-0.5 p-2">
          {NAV_ITEMS.map(({ id, title, icon: Icon }) => {
            const isActive = id === focusedActiveViewId
            const path = getViewPath(id)
            const button = (
              <a
                href={path}
                onClick={(e) => onNavClick(e, id)}
                className={cn(
                  'flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                  collapsed && 'justify-center px-2',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {!collapsed && <span>{title}</span>}
              </a>
            )
            if (collapsed) {
              return (
                <Tooltip key={id}>
                  <TooltipTrigger asChild>{button}</TooltipTrigger>
                  <TooltipContent side="right">{title}</TooltipContent>
                </Tooltip>
              )
            }
            return <div key={id}>{button}</div>
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-border p-2 text-[10px] text-muted-foreground font-mono">
          {collapsed ? (
            <div className="text-center">v2</div>
          ) : (
            <div className="flex items-center justify-between">
              <span>V.I.K.I.</span><span>v2</span>
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  )
}
